import { FUNNEL_PLATFORMS } from '../../lib/funnel';
import { outcomeBadges, type OutcomeEvent } from '../../lib/outcomeEvents';
import { readRoomMeta, type RoomMeta } from '../../lib/roomMeta';
import { assigneeText, sessionEnded } from './waterfall-list';

export type DetailSession = {
  platform: string;
  mode: 'auto' | 'human';
  peerKey?: string | null;
  title?: string | null;
  assignee?: string | null;
  lastActivity: string;
  endedAt?: string | null;
};

export type DetailMessage = {
  ts: string;
  meta?: RoomMeta | null;
};

export type SessionDetail = {
  platform: string;
  mode: 'auto' | 'human';
  life: 'alive' | 'ended';
  peerKey: string | null;
  title: string | null;
  assignee: string | null;
  lastActivity: string;
  room: string | null;
  outcomes: OutcomeEvent[];
};

function text(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function platformChip(value: string | null | undefined): string {
  if (value && (FUNNEL_PLATFORMS as readonly string[]).includes(value)) return value;
  return 'all';
}

export function roomText(messages: readonly DetailMessage[]): string | null {
  const ordered = messages.map((row, index) => ({ row, index })).sort((a, b) => {
    if (a.row.ts === b.row.ts) return b.index - a.index;
    return a.row.ts < b.row.ts ? 1 : -1;
  });
  for (const { row } of ordered) {
    const meta = readRoomMeta(row.meta);
    if (!meta) continue;
    const id = text(meta.roomId) || text(meta.room);
    const name = text(meta.roomName);
    if (id && name && name !== id) return `${name} · ${id}`;
    if (id) return id;
    if (name) return name;
  }
  return null;
}

export function sessionDetail(
  session: DetailSession,
  messages: readonly DetailMessage[],
  events: readonly string[],
): SessionDetail {
  const peerKey = text(session.peerKey);
  const title = text(session.title);
  return {
    platform: session.platform,
    mode: session.mode,
    life: sessionEnded(session) ? 'ended' : 'alive',
    peerKey: peerKey || null,
    title: title || null,
    assignee: assigneeText(session.assignee),
    lastActivity: session.lastActivity,
    room: roomText(messages),
    outcomes: outcomeBadges(events),
  };
}
